import {
  BadgeCheck,
  Copy,
  Edit2,
  Link as LinkIcon,
  MessageCircle,
  Power,
  RotateCw,
  Share2,
  ShieldOff,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { Courier, TranslationKey } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/* v8 ignore next -- optional callback defaults are defensive no-ops. */
const noopCourierHandler = () => {};

interface MobileActionButtonProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly icon: LucideIcon;
}

function MobileActionButton({
  label,
  disabled,
  onClick,
  icon: Icon,
}: MobileActionButtonProps): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="h-auto min-h-14 min-w-16 flex-col gap-1 px-2 py-2 text-center"
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="text-muted-foreground max-w-20 whitespace-normal text-[0.68rem] leading-tight">
        {label}
      </span>
    </Button>
  );
}

export interface CouriersMobileCardsProps {
  readonly couriers: readonly Courier[];
  readonly isMutating?: boolean;
  readonly onEdit: (courier: Courier) => void;
  readonly onToggleActive: (courier: Courier) => void;
  readonly onDelete: (courier: Courier) => void;
  readonly onVerify?: (courier: Courier) => void;
  readonly onCopyPortalLink?: (courier: Courier) => void;
  readonly onSharePortalLink?: (courier: Courier) => void;
  readonly onSendPortalWhatsApp?: (courier: Courier) => void;
  readonly onRevokePortalLink?: (courier: Courier) => void;
  readonly onRegeneratePortalLink?: (courier: Courier) => void;
  readonly onGeneratePortalLink?: (courier: Courier) => void;
  readonly isPortalActionPending?: boolean;
}

export function CouriersMobileCards({
  couriers,
  isMutating = false,
  onEdit,
  onToggleActive,
  onDelete,
  onVerify = noopCourierHandler,
  onCopyPortalLink = noopCourierHandler,
  onSharePortalLink = noopCourierHandler,
  onSendPortalWhatsApp = noopCourierHandler,
  onRevokePortalLink = noopCourierHandler,
  onRegeneratePortalLink = noopCourierHandler,
  onGeneratePortalLink = noopCourierHandler,
  isPortalActionPending = false,
}: CouriersMobileCardsProps): React.JSX.Element {
  const { t, dir } = useTranslation();

  return (
    <div dir={dir} data-testid="couriers-mobile-cards" className="flex flex-col gap-3 md:hidden">
      {couriers.map((courier) => {
        const canUsePortal = courier.active && courier.verification_status === 'approved';
        const hasActivePortalLink = courier.portal_link?.status === 'available';
        const disablePortalActions = isMutating || isPortalActionPending;
        return (
          <article
            key={courier.id}
            data-testid="couriers-mobile-card"
            className="border-border bg-card shadow-admin-card rounded-lg border p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-foreground truncate text-base font-semibold">{courier.name}</h2>
                <p dir="ltr" className="text-muted-foreground mt-1 text-start text-sm">
                  {courier.phone_number}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Badge variant={courier.active ? 'success' : 'neutral'}>
                  {t(
                    (courier.active
                      ? 'admin.couriers.status.active'
                      : 'admin.couriers.status.inactive') as TranslationKey,
                  )}
                </Badge>
                <Badge variant={courier.verification_status === 'approved' ? 'success' : 'warning'}>
                  {t(
                    (courier.verification_status === 'approved'
                      ? 'admin.couriers.verification.approved'
                      : 'admin.couriers.verification.pending') as TranslationKey,
                  )}
                </Badge>
              </div>
            </div>

            {courier.internal_notes ? (
              <p className="text-muted-foreground mt-3 line-clamp-2 text-start text-sm">
                {courier.internal_notes}
              </p>
            ) : null}

            {canUsePortal ? (
              <div className="border-border mt-4 flex flex-wrap justify-end gap-2 border-t pt-4">
                {hasActivePortalLink ? (
                  <>
                    <MobileActionButton
                      label={t('admin.couriers.portal.copyLink' as TranslationKey)}
                      disabled={disablePortalActions}
                      onClick={() => onCopyPortalLink(courier)}
                      icon={Copy}
                    />
                    <MobileActionButton
                      label={t('admin.couriers.portal.shareLink' as TranslationKey)}
                      disabled={disablePortalActions}
                      onClick={() => onSharePortalLink(courier)}
                      icon={Share2}
                    />
                    <MobileActionButton
                      label={t('admin.couriers.portal.sendWhatsApp' as TranslationKey)}
                      disabled={disablePortalActions}
                      onClick={() => onSendPortalWhatsApp(courier)}
                      icon={MessageCircle}
                    />
                    <MobileActionButton
                      label={t('admin.couriers.portal.revoke' as TranslationKey)}
                      disabled={disablePortalActions}
                      onClick={() => onRevokePortalLink(courier)}
                      icon={ShieldOff}
                    />
                  </>
                ) : (
                  <MobileActionButton
                    label={t('admin.couriers.portal.generate' as TranslationKey)}
                    disabled={disablePortalActions}
                    onClick={() => onGeneratePortalLink(courier)}
                    icon={LinkIcon}
                  />
                )}
                <MobileActionButton
                  label={t('admin.couriers.portal.regenerate' as TranslationKey)}
                  disabled={disablePortalActions}
                  onClick={() => onRegeneratePortalLink(courier)}
                  icon={RotateCw}
                />
              </div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <MobileActionButton
                label={t('admin.couriers.verification.title' as TranslationKey)}
                disabled={isMutating}
                onClick={() => onVerify(courier)}
                icon={BadgeCheck}
              />
              <MobileActionButton
                label={t('admin.couriers.actions.edit' as TranslationKey)}
                disabled={isMutating}
                onClick={() => onEdit(courier)}
                icon={Edit2}
              />
              <MobileActionButton
                label={t(
                  (courier.active
                    ? 'admin.couriers.actions.deactivate'
                    : 'admin.couriers.actions.activate') as TranslationKey,
                )}
                disabled={isMutating}
                onClick={() => onToggleActive(courier)}
                icon={Power}
              />
              <MobileActionButton
                label={t('admin.couriers.actions.delete' as TranslationKey)}
                disabled={isMutating}
                onClick={() => onDelete(courier)}
                icon={Trash2}
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}
